/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { CountUp } from "countup.js";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftProfile from "../ProfilePictures/SoftProfile";
import YETI_PROFILE from "../../constants/yetiProfile";
import PeakTrophyOverlay from "../Trophy3D/PeakTrophyOverlay";
import { niceAxisMax } from "./rulerScale";
import mountainPeak from "../../img/mountainPeak.jpg";

/**
 * PhotoMountain — the photographic "King of the Mountain" VARIANT.
 *
 * Same live data as the faceted KingOfTheMountain (playerScore / yetiScore /
 * maxPoint / names) — only the artwork changes: a real snow-peak image with the
 * exact climbing trail traced in Figma (Vector 25). The pink/blue climbers ride
 * that trail to their real scores, the ruler reads the real maxPoint, the score
 * cards show real points, and the trophy sits on the summit. Fixed image, so it
 * never needs the per-company recolour the faceted mountain does.
 */

// Figma frame + trail geometry. The frame is 1961x1307; the trail (Vector 25)
// lives in its own 615.154x758.927 box placed at (393,277) and scaled to fit
// the 602x754 slot it occupies in the frame.
const FRAME_W = 1961;
const FRAME_H = 1307;
const TRAIL_TX = 393;
const TRAIL_TY = 277;
const TRAIL_SX = 602 / 615.154;
const TRAIL_SY = 754 / 758.927;
const TRAIL_LOCAL_H = 758.927; // trail-local: y=0 is the summit, y≈754 the base
const TRAIL_D =
  "M5.65432 754L66.6543 684L112.154 663L142.654 608H191.154L245.654 544.5L312.654 462.5L398.154 401.5H449.654L516.654 334.5L583.154 246.5V115.5L607.654 45.5V0";

// Ruler sits on the right sky; its top tick = the summit height, bottom = base.
const RULER_X = 1892;

// A trail-local point → frame coordinates.
const toFrame = (p) => ({ x: TRAIL_TX + p.x * TRAIL_SX, y: TRAIL_TY + p.y * TRAIL_SY });

// Stopping point on the trail whose HEIGHT matches the ruler value for `pts`,
// so a climber's marker reads the correct number on the ruler (same approach as
// the faceted board). Returns the length fraction along the path.
const fracForScore = (lineId, pts, maxPoint) => {
  const frac = Math.min((pts || 0) / (maxPoint || 1), 0.999);
  const path = typeof document !== "undefined" && document.querySelector(`#${lineId}`);
  if (!path || !path.getTotalLength) return frac;
  const targetY = TRAIL_LOCAL_H * (1 - frac); // summit=0, base=~754
  const total = path.getTotalLength();
  const steps = 260;
  for (let i = 0; i <= steps; i += 1) {
    const l = (i / steps) * total;
    if (path.getPointAtLength(l).y <= targetY) {
      if (i === 0) return 0;
      let lo = ((i - 1) / steps) * total;
      let hi = l;
      for (let b = 0; b < 24; b += 1) {
        const mid = (lo + hi) / 2;
        if (path.getPointAtLength(mid).y <= targetY) hi = mid;
        else lo = mid;
      }
      return Math.min(hi / total, 0.999);
    }
  }
  return 0.999;
};

const PhotoMountain = ({
  cardTitle,
  maxPoint,
  yetiScore,
  playerScore,
  playerName,
  playerLogo,
  darkMode,
  noScroll,
  peakTrophy,
  dashboard,
}) => {
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;
  const yetiColor = "#EC2786";

  // Ruler top rounded up to a round number — see ./rulerScale. Used for both the
  // tick labels and the marker placement so the two always agree.
  const axisMax = niceAxisMax(maxPoint);

  const boardRef = useRef();
  const svgRef = useRef();
  const playerPtsRef = useRef();
  const yetiPtsRef = useRef();
  const isSmallScreen = useMediaQuery((t) => t.breakpoints.down("md"));

  // Count the two point numbers up on mount / when scores change.
  useEffect(() => {
    if (!maxPoint) return;
    const p = Number.isFinite(playerScore) ? playerScore : 0;
    new CountUp(playerPtsRef.current, p, { duration: 2.2, useEasing: true, separator: "," }).start();
    new CountUp(yetiPtsRef.current, yetiScore || 0, { duration: 2.2, useEasing: true, separator: "," }).start();
  }, [maxPoint, playerScore, yetiScore]);

  // Place each climber marker on the trail at the point that matches its score.
  // Runs after paint and re-settles on delayed timeouts (the grid re-renders a
  // lot) so the marker always lands on the final settled score.
  useEffect(() => {
    if (!maxPoint) return undefined;
    const place = () => {
      [
        ["pm-person1", playerScore],
        ["pm-person2", yetiScore],
      ].forEach(([name, pts]) => {
        const line = document.querySelector(`#${name}-line`);
        const grp = document.querySelector(`#end-g-${name}`);
        if (!line || !grp || !line.getTotalLength) return;
        const total = line.getTotalLength();
        const frac = fracForScore(`${name}-line`, pts, axisMax);
        const local = line.getPointAtLength(frac * total);
        const f = toFrame(local);
        gsap.to(grp, { attr: { transform: `translate(${f.x},${f.y})` }, duration: 1.1, ease: "sine.out", overwrite: "auto" });
      });
    };
    const raf = requestAnimationFrame(place);
    const t1 = setTimeout(place, 500);
    const t2 = setTimeout(place, 1600);
    const t3 = setTimeout(place, 3200);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [maxPoint, playerScore, yetiScore]);

  // Ruler ticks — top = summit (max), bottom = trail base (0). The top is rounded
  // up so every tick reads as a round number instead of an eighth of a raw score;
  // markers are placed against the same value, so each still sits at its exact
  // score. Matches the faceted board (see ./rulerScale).
  const rulerTop = TRAIL_TY; // summit y in frame
  const rulerBot = TRAIL_TY + TRAIL_SY * 754; // base y in frame
  const ticks = Array.from({ length: 9 }, (_, i) => {
    const value = (axisMax * (8 - i)) / 8;
    const y = rulerTop + (i / 8) * (rulerBot - rulerTop);
    return { value, y, major: i % 2 === 0 };
  });

  const tickColor = darkMode ? "#FFFFFF" : "#0F172A";

  // Whoever is ahead on points sits on top; the two cards swap the instant one
  // overtakes the other.
  const playerLeads = (Number(playerScore) || 0) >= (Number(yetiScore) || 0);

  if (!(maxPoint > 0)) return null;

  return (
    <SoftBox className="w-full relative" sx={{ zIndex: 1, paddingTop: { mini: dashboard ? 0 : "24px", lg: 0 }, paddingLeft: { mini: dashboard ? 0 : "1rem", sm: dashboard ? 0 : "1.5rem", lg: 0 }, paddingRight: { mini: dashboard ? 0 : "1rem", sm: dashboard ? 0 : "1.5rem", lg: 0 } }}>
      <SoftBox
        shadow="md"
        ref={boardRef}
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "30vh",
          position: "relative",
          overflow: "hidden",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
        bgColor={darkMode ? "dark" : "white"}
      >
        {/* Title strip — matches the faceted board */}
        <SoftBox
          sx={{
            height: "56px",
            paddingLeft: { mini: "16px", sm: "20px" },
            paddingRight: { mini: "16px", sm: "20px" },
            borderBottom: darkMode ? "1px solid #2A2F36" : "1px solid #EAECF0",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flex: "0 0 auto",
          }}
        >
          <SoftTypography
            sx={{
              fontFamily: "DM Sans",
              fontSize: "16px",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: darkMode ? "#FFFFFF" : "#0F172A",
              textFillColor: darkMode ? "#FFFFFF" : "#0F172A",
              lineHeight: 1.2,
            }}
          >
            {cardTitle || "King of the Mountain"}
          </SoftTypography>
        </SoftBox>

        {/* Mountain stage — image + trail + climbers + ruler share ONE SVG so
            they always scale together and stay pixel-aligned. */}
        <SoftBox sx={{ position: "relative", flex: 1, minHeight: 0, width: "100%" }}>
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox={`0 0 ${FRAME_W} ${FRAME_H}`}
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, display: "block" }}
          >
            <image href={mountainPeak} x="0" y="0" width={FRAME_W} height={FRAME_H} preserveAspectRatio="none" />

            {/* On a dark TV the daylight photo glares as a white block. A graphite
                scrim (heaviest on the bright sky, lighter over the mountain body,
                a touch more on the cloud base) blends it into the dark card so it
                reads as a dusk peak. Sits above the image, below trail/markers. */}
            {darkMode && (
              <>
                <defs>
                  <linearGradient id="pm-dark-scrim" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#0E1220" stopOpacity="0.84" />
                    <stop offset="0.42" stopColor="#0E1220" stopOpacity="0.5" />
                    <stop offset="1" stopColor="#0E1220" stopOpacity="0.62" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width={FRAME_W} height={FRAME_H} fill="url(#pm-dark-scrim)" />
              </>
            )}

            {/* Trail (Figma Vector 25) — drawn in its own scaled group */}
            <g transform={`translate(${TRAIL_TX},${TRAIL_TY}) scale(${TRAIL_SX},${TRAIL_SY})`}>
              <defs>
                <linearGradient id="pm-trail" x1="534.154" y1="0" x2="158.654" y2="699.5" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#004665" />
                  <stop offset="1" stopColor="#FFFFFF" />
                </linearGradient>
              </defs>
              <path d={TRAIL_D} stroke="url(#pm-trail)" strokeWidth="15" strokeDasharray="16 16" fill="none" strokeLinecap="round" />
            </g>

            {/* Measurement copies of the trail (untransformed local coords) so
                getPointAtLength resolves cleanly; hidden from view. */}
            <path id="pm-person1-line" d={TRAIL_D} fill="none" stroke="none" style={{ visibility: "hidden" }} />
            <path id="pm-person2-line" d={TRAIL_D} fill="none" stroke="none" style={{ visibility: "hidden" }} />

            {/* Ruler */}
            <g>
              <line x1={RULER_X} y1={rulerTop} x2={RULER_X} y2={rulerBot} stroke={primaryColor} strokeWidth="2" strokeLinecap="round" />
              {ticks.map((t) => (
                <g key={t.y}>
                  <line x1={t.major ? RULER_X - 16 : RULER_X - 9} y1={t.y} x2={RULER_X} y2={t.y} stroke={primaryColor} strokeWidth="2" strokeLinecap="round" />
                  {t.major && (
                    <text x={RULER_X - 22} y={t.y + 6} fontFamily="DM Sans" fontSize="40" fontWeight="600" textAnchor="end" fill={tickColor}>
                      {t.value.toLocaleString()}
                    </text>
                  )}
                </g>
              ))}
            </g>

            {/* Yeti connector + marker */}
            <g id="end-g-pm-person2" transform={`translate(${toFrame({ x: 5.65, y: 754 }).x},${toFrame({ x: 5.65, y: 754 }).y})`}>
              <line x1="0" y1="0" x2={RULER_X} y2="0" stroke={yetiColor} strokeWidth="3" strokeDasharray="2 6" strokeLinecap="round" opacity="0.9" />
              <circle cx="0" cy="0" r="16" fill={yetiColor} stroke="#FFFFFF" strokeWidth="4" />
            </g>
            {/* Player connector + marker */}
            <g id="end-g-pm-person1" transform={`translate(${toFrame({ x: 5.65, y: 754 }).x},${toFrame({ x: 5.65, y: 754 }).y})`}>
              <line x1="0" y1="0" x2={RULER_X} y2="0" stroke={primaryColor} strokeWidth="3" strokeDasharray="2 6" strokeLinecap="round" opacity="0.9" />
              <circle cx="0" cy="0" r="16" fill={primaryColor} stroke="#FFFFFF" strokeWidth="4" />
            </g>
          </svg>

          {peakTrophy && <PeakTrophyOverlay svgRef={svgRef} peakX={988} peakY={277} vbSize={160} baseFactor={1.02} />}

          {/* Score cards — same live data as the faceted board */}
          {/* Both cards stacked top-left; each border tinted with its climber's
              colour (player = brand blue, yeti = pink) so you can read which is
              which at a glance. */}
          <SoftBox
            sx={{
              position: "absolute",
              top: { mini: "7%", sm: "6%" },
              left: { mini: "5%", md: "4%" },
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              pointerEvents: "none",
            }}
          >
            <SoftBox
              shadow="sm"
              sx={{
                order: playerLeads ? 0 : 1,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: darkMode ? "rgba(13,13,13,0.85)" : "rgba(255,255,255,0.94)",
                backdropFilter: "blur(6px)",
                border: `1px solid ${primaryColor}`,
                borderRadius: "12px",
                padding: "7px 12px",
              }}
            >
              <SoftProfile size="sm" src={playerLogo} name={playerName} fullCover />
              <SoftBox>
                <SoftTypography
                  sx={{ fontFamily: "DM Sans", fontSize: { mini: "16px", md: "20px" }, fontWeight: 700, lineHeight: 1.1, color: darkMode ? "#fff" : "#0F172A" }}
                >
                  <span ref={playerPtsRef}>{Number.isFinite(playerScore) ? Math.round(playerScore) : 0}</span>
                </SoftTypography>
                <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "12px", color: darkMode ? "#B9C0C9" : "#667085" }}>
                  Points
                </SoftTypography>
              </SoftBox>
            </SoftBox>

            <SoftBox
              shadow="sm"
              sx={{
                order: playerLeads ? 1 : 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: darkMode ? "rgba(13,13,13,0.85)" : "rgba(255,255,255,0.94)",
                backdropFilter: "blur(6px)",
                border: `1px solid ${yetiColor}`,
                borderRadius: "12px",
                padding: "7px 12px",
              }}
            >
              <SoftProfile size="sm" src={YETI_PROFILE.image} name={YETI_PROFILE.firstName} />
              <SoftBox>
                <SoftTypography
                  sx={{ fontFamily: "DM Sans", fontSize: { mini: "16px", md: "20px" }, fontWeight: 700, lineHeight: 1.1, color: darkMode ? "#fff" : "#0F172A" }}
                >
                  <span ref={yetiPtsRef}>{Math.round(yetiScore || 0)}</span>
                </SoftTypography>
                <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "12px", color: darkMode ? "#B9C0C9" : "#667085" }}>
                  Points
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default PhotoMountain;
